import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Toggle from '/@/components/forms/toggle';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { isMobile } from '/@/utils';
import Input from '/@/v3/components/forms/input';
import styles from './styles.module.css';

@autobind
export default class FixedEnvironmentTable extends Component {
    static contextTypes = {
        units: PropTypes.object,
        print: PropTypes.bool,
    };

    getRecommendedHarvestDensity(productRecommendation) {
        return !isNaN(productRecommendation.harvestDensity.recommended)
            ? this.context.units.toDensity(productRecommendation.harvestDensity.recommended, 'corn').localeValue
            : '-';
    }

    getEstimatedYieldForRecommendedHarvestDensity(productRecommendation) {
        return !isNaN(productRecommendation.estimatedYield.forRecommendedHarvestDensity)
            ? this.context.units.toYield(productRecommendation.estimatedYield.forRecommendedHarvestDensity, 'corn')
                  .localeValue
            : '-';
    }

    getUserHarvestDensity(productRecommendation) {
        return productRecommendation.harvestDensity.user
            ? this.context.units.toDensity(productRecommendation.harvestDensity.user, 'corn').localeValue
            : this.renderNotAvailable();
    }

    getUserNitrogen() {
        return this.props.userNitrogen || '';
    }

    getEstimatedYieldForUserHarvestDensity(productRecommendation) {
        productRecommendation.estimatedYield.forUserHarvestDensity;
        return productRecommendation.estimatedYield.forUserHarvestDensity
            ? this.context.units.toYield(productRecommendation.estimatedYield.forUserHarvestDensity, 'corn').localeValue
            : this.renderNotAvailable();
    }

    getEstimatedYieldDiff(productRecommendation) {
        return productRecommendation.estimatedYield.differentials &&
            productRecommendation.estimatedYield.differentials.forUserHarvestDensity
            ? this.context.units.toYield(
                  productRecommendation.estimatedYield.differentials.forUserHarvestDensity.value,
                  'corn'
              ).localeValue
            : this.renderNotAvailable();
    }

    getEstimatedYieldDiffPercentage(productRecommendation) {
        return productRecommendation.estimatedYield.differentials &&
            productRecommendation.estimatedYield.differentials.forUserHarvestDensity
            ? this.context.units.toPercentage(
                  productRecommendation.estimatedYield.differentials.forUserHarvestDensity.percentage
              ).localeValue
            : this.renderNotAvailable();
    }

    shouldShowRecommendedNitrogen(productRecommendations) {
        return productRecommendations.every(
            ({ fertilization }) => fertilization && fertilization.n && fertilization.n.recommended
        );
    }

    handleProductToggle(id) {
        return () => this.props.onProductToggle(id);
    }

    getDensitySource() {
        // If we don't have points to build the density curve it means that the density value
        // is defined for a specific range.
        const hasDensityCurve = this.props.productRecommendations.reduce(
            (current, { estimatedYieldByHarvestDensity }) => estimatedYieldByHarvestDensity || current,
            false
        );
        return hasDensityCurve ? 'fromCoefficients' : 'fromRanges';
    }

    renderEnvironmentRange(range) {
        // TODO: Handle cases when range has only a single value
        const limits = ['minEnvironment', 'maxEnvironment'].map((key) =>
            range[key] === null ? ' - ' : this.context.units.toYield(range[key], 'corn').localeValue
        );
        return `${limits.join(' ~ ')} ${this.context.units.getUnit('yield', 'corn')}`;
    }

    renderDensityTableFromRanges({ productRecommendations, theme, rootClassName }) {
        return (
            <table className={classNames(rootClassName, styles.table)}>
                <thead>
                    <tr>
                        <th />
                        {productRecommendations.map((productRecommendation) => (
                            <th key={productRecommendation.id}>
                                <span className={styles.group}>
                                    {this.renderProductName(productRecommendation, theme, false)}
                                </span>
                            </th>
                        ))}
                    </tr>
                    <tr className={styles.unit}>
                        <th>{_('Rango de ambiente')}</th>
                        {productRecommendations.map((productRecommendation) => (
                            <th key={productRecommendation.id}>{this.context.units.getUnit('density', 'corn')}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {this.getEnvironmentRanges(productRecommendations).map((range, i) => {
                        return (
                            <tr key={i}>
                                <td className={styles.environmentRange}>{this.renderEnvironmentRange(range)}</td>
                                {productRecommendations.map((productRecommendation, index) => (
                                    <td key={index}>
                                        {this.belongsToRange(productRecommendation, range)
                                            ? this.getRecommendedHarvestDensity(productRecommendation)
                                            : '-'}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderDensityTableFromCoefficients({ productRecommendations, showRecommendedNitrogen, theme, bgRecommended }) {
        return (
            <table className={classNames(styles.rootDesktop, styles.table)}>
                <thead>
                    <tr className={styles.group}>
                        <th rowSpan={3} />
                        <th colSpan={showRecommendedNitrogen ? 3 : 2} className={bgRecommended}>
                            {_('Recomendado')}
                        </th>
                        <th colSpan={3} className={styles.bgUser}>
                            {_('Habitual')}
                        </th>
                        <th colSpan={2} className={styles.bgDiff}>
                            {_('Diferencial')}
                        </th>
                    </tr>
                    <tr className={styles.unitType}>
                        <th className={bgRecommended}>{_('Densidad')}</th>
                        {showRecommendedNitrogen && <th className={bgRecommended}>{_('N')}</th>}
                        <th className={bgRecommended}>{_('Rendimiento')}</th>
                        <th className={styles.bgUser}>{_('Densidad')}</th>
                        <th className={styles.bgUser}>{_('N')}</th>
                        <th className={styles.bgUser}>{_('Rendimiento')}</th>
                        <th className={styles.bgDiff} colSpan={2}>
                            {_('Rendimiento')}
                        </th>
                    </tr>
                    <tr className={styles.unit}>
                        <th className={bgRecommended}>{this.context.units.getUnit('density', 'corn')}</th>
                        {showRecommendedNitrogen && <th className={bgRecommended}>kg/ha</th>}
                        <th className={bgRecommended}>{this.context.units.getUnit('yield', 'corn')}</th>
                        <th className={styles.bgUser}>{this.context.units.getUnit('density', 'corn')}</th>
                        <th className={styles.bgUser}>kg/ha</th>
                        <th className={styles.bgUser}>{this.context.units.getUnit('yield', 'corn')}</th>
                        <th className={styles.bgDiff}>{this.context.units.getUnit('yield', 'corn')}</th>
                        <th className={styles.bgDiff}>%</th>
                    </tr>
                </thead>
                <tbody>
                    {productRecommendations.map((productRecommendation) => (
                        <tr key={productRecommendation.id}>
                            <td>
                                <div
                                    className={classNames(styles.productName, {
                                        [styles.selected]: productRecommendation.selected,
                                    })}
                                    onClick={this.handleProductToggle(productRecommendation.id)}
                                >
                                    {this.renderProductName(productRecommendation, theme, true)}
                                </div>
                            </td>
                            <td className={classNames(bgRecommended, styles.borderBottomLeftRadius)}>
                                {this.getRecommendedHarvestDensity(productRecommendation)}
                            </td>
                            {showRecommendedNitrogen && (
                                <td className={bgRecommended}>
                                    {productRecommendation.fertilization.n.recommended
                                        ? `${productRecommendation.fertilization.n.recommended.toFixed(2)} kg/ha`
                                        : '-'}
                                </td>
                            )}
                            <td className={classNames(bgRecommended, styles.borderBottomRightRadius)}>
                                {this.getEstimatedYieldForRecommendedHarvestDensity(productRecommendation)}
                            </td>
                            <td className={styles.bgUser}>{this.getUserHarvestDensity(productRecommendation)}</td>
                            <td className={styles.bgUser}>{this.getUserNitrogen()}</td>
                            <td className={styles.bgUser}>
                                {this.getEstimatedYieldForUserHarvestDensity(productRecommendation)}
                            </td>
                            <td className={classNames(styles.bgDiff, styles.borderBottomLeftRadius)}>
                                {this.getEstimatedYieldDiff(productRecommendation)}
                            </td>
                            <td className={classNames(styles.bgDiff, styles.borderBottomRightRadius)}>
                                {this.getEstimatedYieldDiffPercentage(productRecommendation)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    renderMobile({ productRecommendation, showRecommendedNitrogen, theme, densitySource }) {
        const bgRecommended = classNames(styles.bgRecommended, styles[`theme-${theme}`]);

        if (densitySource === 'fromRanges') {
            return this.renderDensityTableFromRanges({
                theme,
                productRecommendations: [productRecommendation],
                rootClassName: styles.rootMobile,
            });
        }

        return (
            <div className={styles.rootMobile}>
                <table className={styles.table}>
                    <thead>
                        <tr className={styles.group}>
                            <th />
                            <th className={bgRecommended}>{_('Recomendado')}</th>
                            <th className={styles.bgUser}>{_('Habitual')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <div className={styles.unitType}>{_('Densidad')}</div>
                                <div className={styles.unit}>{this.context.units.getUnit('density', 'corn')}</div>
                            </td>
                            <td className={bgRecommended}>
                                {this.getRecommendedHarvestDensity(productRecommendation)}
                            </td>
                            <td className={styles.bgUser}>{this.getUserHarvestDensity(productRecommendation)}</td>
                        </tr>

                        <tr>
                            <td>
                                <div className={styles.unitType}>{_('N')}</div>
                                <div className={styles.unit}>{this.context.units.getUnit('weight/area')}</div>
                            </td>
                            <td className={bgRecommended}>
                                {productRecommendation.fertilization.n.recommended
                                    ? `${productRecommendation.fertilization.n.recommended.toFixed(2)} kg/ha`
                                    : '-'}
                            </td>
                            <td className={styles.bgUser}>{this.getUserNitrogen()}</td>
                        </tr>

                        <tr>
                            <td>
                                <div className={styles.unitType}>{_('Rendimiento')}</div>
                                <div className={styles.unit}>{this.context.units.getUnit('yield', 'corn')}</div>
                            </td>
                            <td className={bgRecommended}>
                                {this.getEstimatedYieldForRecommendedHarvestDensity(productRecommendation)}
                            </td>
                            <td className={styles.bgUser}>
                                {this.getEstimatedYieldForUserHarvestDensity(productRecommendation)}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className={classNames(styles.diff, styles.bgDiff)}>
                    <div className={styles.group}>{_('Diferencial')}:</div>
                    <div className={styles.value}>
                        <div>{this.getEstimatedYieldDiff(productRecommendation)}</div>
                        <div className={styles.unit}>{this.context.units.getUnit('yield', 'corn')}</div>
                    </div>
                    <div className={styles.value}>
                        <div>{this.getEstimatedYieldDiffPercentage(productRecommendation)}</div>
                        <div className={styles.unit}>%</div>
                    </div>
                </div>
            </div>
        );
    }

    renderDesktop({ productRecommendations, showRecommendedNitrogen, theme, densitySource }) {
        if (densitySource === 'fromCoefficients') {
            const bgRecommended = classNames(styles.bgRecommended, styles[`theme-${theme}`]);
            return this.renderDensityTableFromCoefficients({
                productRecommendations,
                showRecommendedNitrogen,
                theme,
                bgRecommended,
            });
        }

        if (densitySource === 'fromRanges') {
            return this.renderDensityTableFromRanges({
                theme,
                productRecommendations,
                rootClassName: styles.rootDesktop,
            });
        }
    }

    renderFieldLimits(key) {
        const { fieldLimits } = this.props;
        if (!fieldLimits[key]) return null;
        return (
            <div className={styles.helpText}>
                {`${_('Rango')}: ${fieldLimits?.[key]?.min.value} - ${fieldLimits?.[key]?.max}`}
            </div>
        );
    }

    getEnvironmentRanges(productRecommendations) {
        const ranges = [];
        productRecommendations
            .filter(({ harvestDensity }) => Boolean(harvestDensity))
            .forEach(({ harvestDensity }) => {
                ranges.push({
                    minEnvironment: harvestDensity.minEnvironmentForRecommended,
                    maxEnvironment: harvestDensity.maxEnvironmentForRecommended,
                });
            });
        return Array.from(new Set(ranges.map(JSON.stringify))).map(JSON.parse);
    }

    belongsToRange({ harvestDensity }, range) {
        if (!harvestDensity) return false;
        return (
            range.minEnvironment == harvestDensity.minEnvironmentForRecommended &&
            range.maxEnvironment == harvestDensity.maxEnvironmentForRecommended
        );
    }

    renderConfiguration() {
        if (this.getDensitySource() !== 'fromCoefficients') return null;

        return (
            <div className={styles.configuration}>
                <p>{_('Manejo habitual del productor')}:</p>
                <div className={styles.field}>
                    <div className={styles.label}>{_('Densidad')}</div>
                    <div className={styles.input}>
                        <Input
                            type='number'
                            color={settings.app.theme}
                            className={styles.input}
                            suffix={this.context.units.getUnit('density', 'corn')}
                            value={
                                this.props.userDensity !== ''
                                    ? this.context.units.toDensity(this.props.userDensity, 'corn').value
                                    : ''
                            }
                            onChange={this.props.onUserDensityChange}
                            disabled={this.props.status === 'read_only'}
                        />
                        {this.renderFieldLimits('userDensity')}
                    </div>
                </div>
                <div className={styles.field}>
                    <div className={styles.label}>{_('Nitrógeno')}</div>
                    <div className={styles.input}>
                        <Input
                            type='number'
                            color={settings.app.theme}
                            className={styles.input}
                            suffix={'kg/ha'}
                            value={this.getUserNitrogen()}
                            onChange={this.props.onUserNitrogenChange}
                            disabled={this.props.status === 'read_only'}
                        />
                        {this.renderFieldLimits('userNitrogen')}
                    </div>
                </div>
            </div>
        );
    }

    renderNotAvailable() {
        return <span className={styles.notAvailable}>-</span>;
    }

    renderProductName(productRecommendation, theme, enableSelection) {
        const {
            profile: { label },
            selected: value,
        } = productRecommendation;
        if (!enableSelection) return label;
        return this.context.print || isMobile() ? label : <Toggle type='checkbox' {...{ theme, label, value }} />;
    }

    render() {
        const { productRecommendations, selectedProductRecommendation } = this.props;
        if (!selectedProductRecommendation) return null;
        const theme =
            settings.app.theme === 'expertobayer'
                ? 'expertobayer'
                : selectedProductRecommendation.profile.product.brand.theme;
        const showRecommendedNitrogen = this.shouldShowRecommendedNitrogen(productRecommendations);

        const densitySource = this.getDensitySource();

        return (
            <div className={styles.root}>
                {this.renderConfiguration()}
                {this.renderMobile({
                    productRecommendation: selectedProductRecommendation,
                    showRecommendedNitrogen,
                    theme,
                    densitySource,
                })}
                {this.renderDesktop({ productRecommendations, showRecommendedNitrogen, theme, densitySource })}
            </div>
        );
    }
}
